import { useState } from 'react';
import { Link2, Loader2, ShoppingBag, Truck } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { StatusPill, type StatusTone } from '@/components/ui/status-pill';
import { ImageCarousel } from '@/components/image-carousel';
import { form_error_message } from '@/lib/forms';
import {
  use_vendor_shipments,
  use_transition_commerce_shipment,
  use_set_shipment_tracking,
} from '@/features/commerce/hooks';
import type { CommerceShipment, CommerceShipmentIntent, CommerceShipmentStatus } from '@/features/commerce/types';

const STATUS_TONE: Record<CommerceShipmentStatus, StatusTone> = {
  PENDING_PAYMENT: 'warning', // never surfaced to vendors, but keeps the map exhaustive
  PLACED: 'warning',
  ACCEPTED: 'info',
  OUT_FOR_DELIVERY: 'info',
  READY: 'info',
  COMPLETED: 'success',
  CANCELLED: 'destructive',
  REJECTED: 'destructive',
};

const STATUS_FILTERS: { label: string; status?: CommerceShipmentStatus }[] = [
  { label: 'All', status: undefined },
  { label: 'Placed', status: 'PLACED' },
  { label: 'Accepted', status: 'ACCEPTED' },
  { label: 'Out for delivery', status: 'OUT_FOR_DELIVERY' },
  { label: 'Ready', status: 'READY' },
  { label: 'Completed', status: 'COMPLETED' },
  { label: 'Cancelled', status: 'CANCELLED' },
  { label: 'Rejected', status: 'REJECTED' },
];

/** Next actions available from a given shipment status. Fulfilment is always DELIVERY for
 *  commerce orders, but the backend still allows a READY (self-pickup-style) completion path
 *  alongside OUT_FOR_DELIVERY, so both are offered once accepted. */
function actions(s: CommerceShipment): { intent: CommerceShipmentIntent; label: string; variant?: 'gradient' | 'outline' | 'ghost' }[] {
  switch (s.status) {
    case 'PLACED':
      return [
        { intent: 'accept', label: 'Accept', variant: 'gradient' },
        { intent: 'reject', label: 'Reject', variant: 'ghost' },
      ];
    case 'ACCEPTED':
      return [
        { intent: 'out-for-delivery', label: 'Out for delivery', variant: 'gradient' },
        { intent: 'ready', label: 'Mark ready', variant: 'outline' },
        { intent: 'cancel', label: 'Cancel', variant: 'ghost' },
      ];
    case 'OUT_FOR_DELIVERY':
    case 'READY':
      return [{ intent: 'complete', label: 'Mark completed', variant: 'gradient' }];
    default:
      return [];
  }
}

function TrackingForm({ shipment }: { shipment: CommerceShipment }) {
  const [ref, set_ref] = useState(shipment.tracking_ref ?? '');
  const [url, set_url] = useState(shipment.tracking_url ?? '');
  const track_m = use_set_shipment_tracking();

  async function save() {
    await track_m.mutateAsync({
      id: shipment.id,
      input: { tracking_ref: ref.trim() || undefined, tracking_url: url.trim() || undefined },
    });
  }

  return (
    <div className="mt-3 flex flex-col gap-2 rounded-input border-2 border-brand-outline-variant/15 bg-secondary/30 p-3" data-testid={`tracking-form-${shipment.id}`}>
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Truck className="h-3.5 w-3.5" /> Tracking
      </p>
      <div className="flex flex-wrap gap-2">
        <Input
          value={ref}
          onChange={(e) => set_ref(e.target.value)}
          placeholder="Tracking reference"
          className="flex-1"
          data-testid={`tracking-ref-${shipment.id}`}
        />
        <Input
          value={url}
          onChange={(e) => set_url(e.target.value)}
          placeholder="Tracking URL"
          className="flex-1"
          data-testid={`tracking-url-${shipment.id}`}
        />
        <Button size="sm" variant="outline" disabled={track_m.isPending} onClick={save} data-testid={`tracking-save-${shipment.id}`}>
          {track_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save
        </Button>
      </div>
      {shipment.tracking_url && (
        <a
          href={shipment.tracking_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          <Link2 className="h-3.5 w-3.5" /> {shipment.tracking_ref || shipment.tracking_url}
        </a>
      )}
    </div>
  );
}

function ShipmentCard({ shipment }: { shipment: CommerceShipment }) {
  const transition = use_transition_commerce_shipment();
  const act = (intent: CommerceShipmentIntent) => {
    const reason = intent === 'reject' || intent === 'cancel' ? window.prompt('Reason (optional):') ?? undefined : undefined;
    transition.mutate({ id: shipment.id, intent, reason: reason?.trim() || undefined });
  };

  return (
    <Card data-testid={`commerce-shipment-${shipment.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Truck className="h-5 w-5 text-primary" />
          <StatusPill tone="neutral">{shipment.vertical === 'PHARMACY' ? 'Pharmacy' : 'Food'}</StatusPill>
          <StatusPill tone={STATUS_TONE[shipment.status]}>{shipment.status.replace(/_/g, ' ').toLowerCase()}</StatusPill>
          {shipment.prescription_required && (
            <StatusPill tone="warning" data-testid={`rx-shipment-badge-${shipment.id}`}>
              Rx
            </StatusPill>
          )}
        </div>
        <span className="font-heading text-lg font-bold tabular-nums">₹{shipment.subtotal_inr}</span>
      </div>

      <div className="p-4">
        <ul className="mb-3 flex flex-col gap-1 text-sm">
          {shipment.lines.map((it) => (
            <li key={it.id} className="flex justify-between">
              <span>
                {it.quantity}× {it.name}
                {it.is_prescription_required && <span className="ml-1 text-xs font-semibold text-warning">Rx</span>}
              </span>
              <span className="tabular-nums text-muted-foreground">₹{it.line_total_inr}</span>
            </li>
          ))}
        </ul>
        <div className="text-xs text-muted-foreground">
          <span>📞 {shipment.contact_phone}</span>
          {shipment.address && (
            <span className="ml-3">📍 {[shipment.address, shipment.landmark, shipment.city, shipment.pincode].filter(Boolean).join(', ')}</span>
          )}
          {shipment.notes && <p className="mt-1">Note: {shipment.notes}</p>}
        </div>

        {shipment.prescription_required && shipment.prescriptions.length > 0 && (
          <div className="mt-3" data-testid={`shipment-prescription-images-${shipment.id}`}>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Prescription</p>
            <ImageCarousel images={shipment.prescriptions} />
          </div>
        )}

        {actions(shipment).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {actions(shipment).map((a) => (
              <Button
                key={a.intent}
                size="sm"
                variant={a.variant}
                disabled={transition.isPending}
                onClick={() => act(a.intent)}
                data-testid={`shipment-${a.intent}-${shipment.id}`}
              >
                {a.label}
              </Button>
            ))}
          </div>
        )}

        {shipment.status !== 'PLACED' && shipment.status !== 'PENDING_PAYMENT' && <TrackingForm shipment={shipment} />}
      </div>
    </Card>
  );
}

/** Vendor shipments inbox — usable by both FOOD and PHARMACY vendors (a shipment is one
 *  vendor's slice of a cross-vendor commerce order). Mirrors the legacy pharmacy-orders-page. */
export function CommerceShipmentsPage() {
  const [filter_idx, set_filter_idx] = useState(0);
  const status = STATUS_FILTERS[filter_idx].status;
  const shipments_q = use_vendor_shipments(status);
  const shipments = shipments_q.data ?? [];

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader title="Orders" description="Incoming paid orders — fulfil and share tracking with the customer." />

      <div className="flex flex-wrap items-center gap-2" data-testid="commerce-shipments-filters">
        {STATUS_FILTERS.map((f, i) => (
          <Button
            key={f.label}
            size="sm"
            variant={i === filter_idx ? 'gradient' : 'ghost'}
            onClick={() => set_filter_idx(i)}
            data-testid={`commerce-shipments-filter-${f.status ?? 'all'}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {shipments_q.isLoading ? (
        <Card>
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        </Card>
      ) : shipments_q.error ? (
        <ErrorState
          data-testid="commerce-shipments-error"
          title="Couldn't load orders"
          description={form_error_message(shipments_q.error, 'Please try again.')}
        />
      ) : shipments.length === 0 ? (
        <EmptyState data-testid="commerce-shipments-empty" icon={ShoppingBag} title="No orders yet" description="New paid orders will appear here." />
      ) : (
        <div className="flex flex-col gap-4" data-testid="commerce-shipments-list">
          {shipments.map((s) => (
            <ShipmentCard key={s.id} shipment={s} />
          ))}
        </div>
      )}
    </div>
  );
}
